import * as React from 'react';
import { useRef, useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Inject, ScheduleComponent, Week, Month, DragAndDrop, ViewsDirective, ViewDirective } from '@syncfusion/ej2-react-schedule';
import { DatePickerComponent } from '@syncfusion/ej2-react-calendars';
import { L10n } from '@syncfusion/ej2-base'
import { RecurrenceEditorComponent } from '@syncfusion/ej2-react-schedule';
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import axios from 'axios';
import ModalCopyProgram from './ModalCopyProgram';
import logoGif from './images/info.gif'
import ModalInfo from './ModalInfo'
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const MemoizedScheduler = React.memo(ScheduleComponent);

/**
 * Extension "X vidéos" — slots au-delà des 5 colonnes historiques.
 * Chaque slot rend un input `name="Videourl{n}"` avec la classe `e-field` : Syncfusion le
 * collecte automatiquement dans args.data au save, où il est sérialisé en `videosJson`
 * (liste JSON des urls 6+). Rétrocompatible : les anciens players ignorent videosJson et
 * lisent Videourl1..5 ; les players >= 5.2.7 concatènent les deux.
 * Inputs NON contrôlés (defaultValue) : la loupe médiathèque écrit .value directement.
 * Défini HORS de Programme() -> identité stable (pas de remount à chaque render parent).
 */
function ExtraVideoSlots({ initialJson, initialReps = [], initialDisps = [], onOpenPicker, onPreview = () => {}, onCopy = () => {} }) {
    const initial = React.useMemo(() => {
        try {
            const a = JSON.parse(initialJson || '[]');
            return Array.isArray(a) ? a.map(String).filter(Boolean) : [];
        } catch (e) { return []; }
    }, [initialJson]);
    const [count, setCount] = React.useState(initial.length);

    const repFor = (i) => {
        const v = String(initialReps[i] || '').trim();
        return v && !Number.isNaN(Number(v)) ? v : '1';
    };
    const dispFor = (i) => String(initialDisps[i] || '').trim();

    return (
        <>
            {Array.from({ length: count }, (_, i) => {
                const n = 6 + i;
                const url = initial[i] || '';
                const fname = url ? url.substring(url.lastIndexOf('/') + 1) : '';
                return (
                    <tr key={`vid-${n}`}><td className="e-textlabel align-top">
                        <div>Vidéo {n}</div>
                        {fname && (
                            <div className="mt-0.5 flex items-center gap-1">
                                <span className="max-w-[90px] truncate text-[10px] font-semibold text-indigo-500" title={url}>{fname}</span>
                                <button type="button" title="Aperçu de la vidéo" onClick={() => onPreview(url)} className="shrink-0 text-slate-400 hover:text-indigo-600">
                                    <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5"><path d="M6.3 2.8A1 1 0 0 0 4.8 3.6v12.8a1 1 0 0 0 1.5.9l10-6.4a1 1 0 0 0 0-1.7l-10-6.4Z" /></svg>
                                </button>
                                <button type="button" title="Copier le nom" onClick={() => onCopy(fname)} className="shrink-0 text-slate-400 hover:text-indigo-600">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-3.5 w-3.5"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>
                                </button>
                            </div>
                        )}
                    </td><td colSpan={4}>
                        <div className="flex items-center w-full gap-2">
                            <div className="flex flex-grow items-center gap-2 min-w-0">
                                <input id={`Videourl${n}`} name={`Videourl${n}`} className="e-field e-input pe-url" type="text"
                                    defaultValue={url} title={url || undefined}
                                    placeholder="URL de la vidéo (loupe = médiathèque, vide = ignoré)" />
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 shrink-0 cursor-pointer text-slate-400 hover:text-indigo-600" onClick={onOpenPicker(`Videourl${n}`)} >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                                </svg>
                            </div>
                            <input id={`dispSlot${n}`} name={`dispSlot${n}`} list="displayIds" className="e-field e-input pe-disp text-center" type="text" title={`id écran / display id de la vidéo ${n}`} placeholder="1" defaultValue={dispFor(i)} />
                            <input id={`repSlot${n}`} name={`repSlot${n}`} className="e-field e-input pe-rep text-center" type="number" min="1" max="99" title={`Nombre de répétitions de la vidéo ${n}`} defaultValue={repFor(i)} />
                        </div>
                    </td></tr>
                );
            })}
            <tr><td></td><td colSpan={4}>
                <button
                    type="button"
                    onClick={() => setCount((c) => c + 1)}
                    className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-dashed border-indigo-300 px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50"
                >
                    + Ajouter une vidéo
                </button>
                <span className="ml-2 text-[10px] text-slate-400">au-delà de 5 : nécessite app ≥ 5.2.7 (les anciennes versions lisent les 5 premières)</span>
            </td></tr>
        </>
    );
}
const DOWNLOAD_STATUS_COLORS = {
    queued: "bg-slate-100 text-slate-700",
    started: "bg-blue-100 text-blue-700",
    progress: "bg-blue-100 text-blue-700",
    retrying: "bg-amber-100 text-amber-700",
    success: "bg-emerald-100 text-emerald-700",
    failed: "bg-red-100 text-red-700",
    using_cached: "bg-teal-100 text-teal-700",
    session_complete: "bg-indigo-100 text-indigo-700"
};

function formatEta(etaSeconds) {
    if (!etaSeconds && etaSeconds !== 0) return "n/a";
    const sec = Number(etaSeconds);
    if (!Number.isFinite(sec) || sec < 0) return "n/a";
    if (sec < 60) return `${sec}s`;
    const min = Math.floor(sec / 60);
    const rem = sec % 60;
    if (min < 60) return `${min}m ${rem}s`;
    const hour = Math.floor(min / 60);
    return `${hour}h ${min % 60}m`;
}

// Heure de fin d'un fichier : l'app émet un timestampMs (-> ts) sur l'event de complétion.
// On l'affiche pour les états terminaux (téléchargé / depuis le cache / échec) ; sinon "—".
function formatDoneAt(file) {
    const done = ["success", "using_cached", "failed"].includes(file?.status);
    if (!done || !file?.ts) return "—";
    const d = new Date(file.ts);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "medium" });
}

function safeProgress(value) {
    const progress = Number(value);
    if (!Number.isFinite(progress)) return 0;
    return Math.max(0, Math.min(100, progress));
}


function Programme() {
    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    const [showModal, setShowModal] = React.useState(false);
    const [modalInfo, setModalInfo] = React.useState(false);

    const [showVideoWallModal, setShowVideoWallModal] = React.useState(false);
    const [videoWallData, setVideoWallData] = React.useState([]);

    const [data_programme, setProgramme] = React.useState([])
    // Clé de REMONTAGE du Scheduler. Symptôme observé : après une sauvegarde, `setProgramme` change
    // la dataSource -> Syncfusion re-render le planning et, combiné aux props recréées à chaque render
    // (editorTemplate/popupOpen/popupClose en .bind(this)), l'ÉDITEUR suivant s'ouvrait cassé (dialogue
    // minuscule/inutilisable) : il fallait fermer puis rouvrir la page pour ré-éditer. On force donc un
    // remontage propre du Scheduler après chaque mutation (save / add / delete) en incrémentant cette
    // clé -> équivalent programmatique du « rouvrir la page », qui est justement l'état sain connu.
    const [schedulerKey, setSchedulerKey] = React.useState(0)
    const remountScheduler = () => setSchedulerKey((k) => k + 1)
    const [clientName, setClientName] = React.useState('Chargement...');
    const [isLoading, setIsLoading] = React.useState(true);
    const [downloadProgress, setDownloadProgress] = useState({});
    const [deviceDownloadStatus, setDeviceDownloadStatus] = useState({ aggregate: {}, files: [] });
    const [isDownloadLoading, setIsDownloadLoading] = useState(true);
    // ÉDITEUR OUVERT : gèle les mises à jour du statut de download.
    // Pourquoi : pendant un téléchargement, le SSE `download_update` arrive toutes les ~800 ms ->
    // setDeviceDownloadStatus -> re-render du parent. Or MemoizedScheduler ne protège RIEN
    // (editorTemplate/popupOpen/popupClose sont passés via .bind(this) et eventSettings est un
    // objet littéral : nouvelles références à chaque render -> React.memo court-circuité).
    // Le Scheduler se re-rendait donc, l'éditeur ouvert était recréé et la SAISIE EN COURS
    // (URLs vidéo...) était perdue. Un ref (pas un state) : le lire ne provoque aucun render.
    const editorOpenRef = useRef(false);

    const [clientsList, setClientsList] = useState([]);
    // Tri du picker médiathèque — REF (jamais un state) : le picker est IMPÉRATIF ; un state
    // re-render Programme et Syncfusion réinitialise l'éditeur ouvert (perte de saisie).
    const pickerSortRef = useRef({ key: 'fileName', dir: 1 });

    // Copie le nom d'un média dans le presse-papier (+ toast).
    const copyMediaName = (name) => {
        try {
            navigator.clipboard.writeText(name);
            toast.info(`Nom copié : ${name}`, { autoClose: 2000 });
        } catch (e) { /* clipboard indisponible (contexte non sécurisé) */ }
    };

    // Aperçu vidéo — IMPÉRATIF (toggle DOM, PAS de state) pour la MÊME raison : ouvrir l'aperçu
    // ne doit pas re-render Programme, sinon l'éditeur Syncfusion ouvert se vide.
    const openPreview = (url) => {
        if (!url) return;
        const modal = document.getElementById('videoPreviewModal');
        const player = document.getElementById('videoPreviewPlayer');
        const title = document.getElementById('videoPreviewTitle');
        if (title) title.textContent = url.substring(url.lastIndexOf('/') + 1);
        if (player) { player.src = url; try { player.load(); player.play(); } catch (e) { /* autoplay */ } }
        if (modal) modal.classList.remove('hidden');
    };
    const closePreview = () => {
        const modal = document.getElementById('videoPreviewModal');
        const player = document.getElementById('videoPreviewPlayer');
        if (player) { try { player.pause(); } catch (e) {} player.removeAttribute('src'); try { player.load(); } catch (e) {} }
        if (modal) modal.classList.add('hidden');
    };

    // Parse "28.7 MB" -> octets (pour trier par taille).
    const sizeToBytes = (s) => {
        const m = /([\d.]+)\s*(B|KB|MB|GB|TB)/i.exec(String(s || ''));
        if (!m) return 0;
        const mult = { B: 1, KB: 1e3, MB: 1e6, GB: 1e9, TB: 1e12 };
        return parseFloat(m[1]) * (mult[m[2].toUpperCase()] || 1);
    };

    let datas = []
    let clients = []

    const searchRef = useRef('');
    const resultsRef = useRef(null);

    const searchClientRef = useRef('');
    const resultsClientRef = useRef(null);

    const tempMasterData = useRef({ IdMaster: null, PinMaster: null });

    let currentPage = 1
    const recordsPerPage = 10;
    let lastIndex = currentPage * recordsPerPage;
    let firstindex = lastIndex - recordsPerPage

    let currentClientPage = 1
    let lastClientIndex = currentClientPage * recordsPerPage;
    let firstClientIndex = lastClientIndex - recordsPerPage

    // videokey / recurrence / actualIdEdit : REFS et non des `let` de corps de composant. Un `let`
    // ici est réinitialisé à CHAQUE re-render, alors que ces variables portent un état ENTRE deux
    // évènements distincts : ouvrir le picker -> cliquer une vidéo (videokey), et ouvrir l'éditeur ->
    // cliquer Enregistrer (recurrence, actualIdEdit). Un re-render survenu entre les deux remettait
    // `videokey` à '' -> setVideoUrl sautait l'écriture de l'URL choisie -> Syncfusion collectait
    // l'ANCIENNE URL au save (bug « il faut enregistrer 2 fois pour changer une vidéo »). Un ref
    // conserve sa valeur à travers les re-renders et corrige le problème de façon déterministe.
    const videokeyRef = useRef('');
    // const records = datas.slice(firstindex, lastIndex)
    const npage = Math.ceil(datas.length / recordsPerPage)
    const numbers = [...Array(npage + 1).keys()].slice(1)


    const recurrenceRef = useRef('');
    let allId = [];
    const actualIdEditRef = useRef('');

    const saveButton = 'e-schedule-dialog e-control e-btn e-lib e-primary e-event-save e-flat';
    const deleteButton = 'e-quick-dialog e-control e-btn e-lib e-quick-alertok e-flat e-primary e-quick-dialog-delete'
    const cancelButton = 'e-schedule-dialog e-control e-btn e-lib e-event-cancel e-flat'

    const values = {
        Id: '',
        _pin_code: '',
        Subject: '',
        Videourl1: '',
        Videourl2: '',
        Videourl3: '',
        repetition: '',
        clientId: '',
        idPdv: '',
        displayId: '',
        StartTime: '',
        EndTime: '',
        heureDebut: '',
        heureFin: '',
        noExp: '',
        RecurrenceRule: '',
        twentyFourH: '',
        Videourl4: '',
        Videourl5: '',
        IdMaster: '',
        PinMaster: '',
    }

    // Mapping des champs : objet PUREMENT STATIQUE (aucune dépendance à un état).
    // useMemo([]) -> référence stable. Sans ça il était recréé à chaque render, donc
    // `eventSettings` aussi -> Syncfusion voyait une "nouvelle" dataSource et RETRAITAIT tout
    // le calendrier à chaque paquet SSE `download_update` (~800 ms) = le clignotement.
    const fieldsData = useMemo(() => ({
        id: 'Id',
        subject: { name: 'Subject' },
        Videourl1: { name: 'Videourl1' },
        Videourl2: { name: 'Videourl2' },
        Videourl3: { name: 'Videourl3' },
        clientId: { name: 'clientId' },
        idPdv: { name: 'idPdv' },
        displayId: { name: 'displayId' },
        startTime: { name: 'StartTime' },
        endTime: { name: 'EndTime' },
        repetition: { name: 'Repetition' },
        heureDebut: { name: 'HeureDebut' },
        heureFin: { name: 'HeureFin' },
        noExp: { name: 'NoExp' },
        RecurrenceRule: { name: 'RecurrenceRule' },
        twentyFourH: { name: 'TwentyFourH' },
        Videourl4: { name: 'Videourl4' },
        Videourl5: { name: 'Videourl5' },
        percentageVideourl1: { name: 'percentageVideourl1' },
        percentageVideourl2: { name: 'percentageVideourl2' },
        percentageVideourl3: { name: 'percentageVideourl3' },
        percentageVideourl4: { name: 'percentageVideourl4' },
        percentageVideourl5: { name: 'percentageVideourl5' },
        IdMaster: { name: 'IdMaster' },
        PinMaster: { name: 'PinMaster' },

    }), []);

    const params = useParams();




    const headers = {
        'x-access-token': token,
        'Content-Type': 'application/json'
    };

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const paraElem = { _pin_code: params._pin_code };

                const nameResponse = await axios.get(`${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}/retriveName/${params._pin_code}`);
                setClientName(nameResponse.data[0]._client_name);

                const programmeResponse = await axios.post(`${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}/get_programme`, paraElem);
                setProgramme(programmeResponse.data);

                const dataAuth = { token: token };
                const clientResponse = await axios.post(`${process.env.REACT_APP_IP2}:${process.env.REACT_APP_PORT2}/admin/auth/listeUsers`, dataAuth, { headers });
                setClientsList(clientResponse.data);
                clients = clientResponse.data;

            } catch (err) {
                console.error("Erreur lors de la récupération des données:", err);
                toast.error("Impossible de charger les données du programme.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [params._pin_code]);

    useEffect(() => {
        let isMounted = true;
        let eventSource = null;
        let refreshTimer = null;

        const scheduleRefresh = () => {
            if (refreshTimer) return;
            refreshTimer = setTimeout(() => {
                refreshTimer = null;
                fetchDownloadStatus();
            }, 800);
        };

        const fetchDownloadStatus = async () => {
            // ÉDITEUR OUVERT -> on ne touche à AUCUN state : le re-render détruirait la saisie
            // en cours (URLs vidéo). Le statut se rafraîchira à la fermeture (SSE + poll 60 s).
            if (editorOpenRef.current) return;
            try {
                const res = await axios.get(`${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}/download_status/device/${params._pin_code}`);
                if (!isMounted) return;

                const payload = res.data || { aggregate: {}, files: [] };
                const files = (payload.files || []).filter((f) => f.fileName !== "_session_");
                const progressMap = {};
                files.forEach((file) => {
                    if (file.fileName) {
                        progressMap[file.fileName] = safeProgress(file.progress);
                    }
                });

                setDeviceDownloadStatus({ ...payload, files });
                setDownloadProgress(progressMap);
            } catch (err) {
                if (!isMounted) return;
                console.error("Erreur lors de la récupération du status download:", err);
            } finally {
                if (isMounted) {
                    setIsDownloadLoading(false);
                }
            }
        };

        setIsDownloadLoading(true);
        fetchDownloadStatus();

        const streamUrl = `${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}/download_status/stream?scope=device&pinCode=${encodeURIComponent(params._pin_code)}`;
        try {
            eventSource = new EventSource(streamUrl);
            eventSource.addEventListener('snapshot', () => {
                scheduleRefresh();
            });
            eventSource.addEventListener('download_update', () => {
                scheduleRefresh();
            });
            eventSource.onerror = () => {
                // Keep silent in UI, fallback polling below will continue.
            };
        } catch (error) {
            console.error("Impossible d'ouvrir le stream SSE device:", error);
        }

        // Fallback léger en cas de perte SSE
        const intervalId = setInterval(fetchDownloadStatus, 60000);

        return () => {
            isMounted = false;
            clearInterval(intervalId);
            if (refreshTimer) {
                clearTimeout(refreshTimer);
            }
            if (eventSource) {
                eventSource.close();
            }
        };
    }, [params._pin_code]);

    L10n.load({
        'fr-CH': {
            'schedule': {
                'saveButton': 'Enregistrer',
                'cancelButton': 'Fermer',
                'deleteButton': 'Supprimer',
                'newEvent': 'Nouveau programme',
                'editEvent': 'Modifier le programme',
                'repeat': 'Récurrence',
            },
            // Éditeur de récurrence Syncfusion (Repeat / Daily / Until… étaient en anglais).
            'recurrenceeditor': {
                'none': 'Aucune',
                'daily': 'Quotidien',
                'weekly': 'Hebdomadaire',
                'monthly': 'Mensuel',
                'yearly': 'Annuel',
                'month': 'Mois',
                'yearlyMonth': 'le',
                'first': 'Premier',
                'second': 'Deuxième',
                'third': 'Troisième',
                'fourth': 'Quatrième',
                'last': 'Dernier',
                'repeat': 'Répéter',
                'repeatEvery': 'Tous les',
                'on': 'Le',
                'end': 'Fin',
                'onDay': 'Jour',
                'days': 'Jour(s)',
                'weeks': 'Semaine(s)',
                'months': 'Mois',
                'years': 'Année(s)',
                'every': 'chaque',
                'summaryTimes': 'fois',
                'summaryOn': 'le',
                'summaryUntil': "jusqu'au",
                'summaryRepeat': 'Répète',
                'summaryDay': 'jour(s)',
                'summaryWeek': 'semaine(s)',
                'summaryMonth': 'mois',
                'summaryYear': 'année(s)',
                'monthWeek': 'Semaine du mois',
                'monthPosition': 'Position dans le mois',
                'monthExpander': 'Détail mensuel',
                'yearExpander': 'Détail annuel',
                'repeatInterval': 'Intervalle',
                'never': 'Jamais',
                'until': "Jusqu'au",
                'count': 'Nombre',
            }
        }
    })

    // Référence stable tant que les programmes ne changent pas : Syncfusion ne retraite sa
    // dataSource que sur un VRAI changement de données, plus à chaque render du parent.
    const eventSettings = useMemo(() => ({ dataSource: data_programme, fields: fieldsData }), [data_programme, fieldsData]);
    const scheduleObj = React.useRef(null);


    function doubleZero(num) {
        return num.toString().padStart(2, '0');
    }

    const handleVideoWallClick = (data) => {
        // On récupère IdMaster/PinMaster depuis les props du formulaire ou les refs temporaires
        const currentIdMaster = data.IdMaster || tempMasterData.current.IdMaster;
        const currentPinMaster = data.PinMaster || tempMasterData.current.PinMaster;

        if (currentIdMaster && currentPinMaster) {
            // On ferme le popup actuel proprement (optionnel, mais propre)
            if (scheduleObj.current) {
                scheduleObj.current.closeEditor();
            }
            // Redirection
            navigate(`/videowall/${currentIdMaster}/${currentPinMaster}`);
        } else {
            toast.info("Ce programme ne fait pas partie d'un groupe Videowall.", {
                position: "top-center",
                autoClose: 3000
            });
        }
    };

    const handlePriceItClick = (data) => {
        // On récupère les valeurs depuis les props (si l'événement existe déjà) 
        // ou depuis les inputs (si l'utilisateur vient de les taper)
        const currentClientId = data.clientId || document.getElementById('clientId')?.value;
        const currentIdPdv = data.idPdv || document.getElementById('idPdv')?.value;

        if (currentClientId && currentIdPdv) {
            // On ferme le popup actuel proprement avant de rediriger
            if (scheduleObj.current) {
                scheduleObj.current.closeEditor();
            }
            // Redirection vers la page ListArticles
            navigate(`/listarticles/${currentIdPdv}/${currentClientId}`);
        } else {
            toast.info("Le Client ID et l'ID PDV sont requis pour accéder au Price It.", {
                position: "top-center",
                autoClose: 3000
            });
        }
    };

    const closePopup = (args) => {
        // Dégel AVANT tout `return` : la garde `!args.data` ci-dessous (annulation / Échap)
        // laisserait sinon le drapeau à true et figerait le statut de download pour toujours.
        if (args.type === 'Editor') editorOpenRef.current = false;
        if (!args.data) return;

        // SOURCE DE VÉRITÉ = la valeur LIVE de l'input dans le DOM (ce que voit l'utilisateur), et NON
        // `args.data[...]` collecté par Syncfusion : cette collecte est NON FIABLE pour nos e-fields
        // custom (Videourl posés par le picker ; dispSlot/repSlot capturés VIDES à l'ouverture et non
        // relus au save). Conséquence directe : avec un clientId Price-Eat, les display id (dispSlot)
        // étaient vus vides -> « Renseignez au moins un écran » bloquait TOUTE sauvegarde alors qu'ils
        // étaient bien remplis (ex. 1-2-3-5;1-2-4-5;…). On lit donc getElementById(id).value ; repli
        // sur args.data seulement si l'input est absent (cas limite). Une chaîne vide est significative
        // (zone/vidéo vide) -> pas de repli quand l'élément existe.
        const domVal = (id, fallbackKey) => {
            const el = document.getElementById(id);
            if (el && typeof el.value === 'string') return el.value.trim();
            const f = fallbackKey ? args.data[fallbackKey] : undefined;
            return f ? String(f).trim() : '';
        };
        const domUrl = (n) => domVal(`Videourl${n}`, `Videourl${n}`);
        const domDisp = (n) => domVal(`dispSlot${n}`, `dispSlot${n}`);
        const domRep = (n) => domVal(`repSlot${n}`, `repSlot${n}`);

        // display_id saisi PAR LIGNE (dispSlot*) : au moins un renseigné si un clientId Price-Eat
        // est fourni. On lit le DOM (cf. ci-dessus) : les slots 1..5 existent toujours, les 6+ sont
        // énumérés via la présence du e-field Videourl correspondant.
        if (args.data.clientId) {
            let anyDisp = false;
            for (let n = 1; n <= 5 && !anyDisp; n++) if (domDisp(n)) anyDisp = true;
            for (let n = 6; !anyDisp && Object.prototype.hasOwnProperty.call(args.data, `Videourl${n}`); n++) if (domDisp(n)) anyDisp = true;
            if (!anyDisp) {
                toast.error("Renseignez au moins un écran (display id) sur une vidéo lorsque le clientId Price-Eat est rempli.");
                return;
            }
        }

        if (recurrenceRef.current) args.data.RecurrenceRule = recurrenceRef.current;

        let heureDebut = args.data.heureDebut.split(":");
        let startDayDate = new Date(args.data.StartTime);
        args.data.StartTime = new Date(startDayDate.getFullYear(), startDayDate.getMonth(), startDayDate.getDate(), heureDebut[0], heureDebut[1]);

        let heureFin = args.data.heureFin.split(":");
        let endDayDate = new Date(args.data.EndTime);
        args.data.EndTime = new Date(endDayDate.getFullYear(), endDayDate.getMonth(), endDayDate.getDate(), heureFin[0], heureFin[1]);

        // Détermine le bouton cliqué de façon ROBUSTE : on remonte au <button> le plus proche (le clic
        // peut atterrir sur un enfant / une icône), et on teste par INCLUSION de classe (comme
        // e-event-cancel), PAS par égalité exacte de toute la chaîne -> sinon un simple changement de
        // classe Syncfusion faisait échouer silencieusement la sauvegarde ("impossible de modifier").
        const clickedBtn = (args.event && args.event.target && args.event.target.closest) ? args.event.target.closest('button') : null;
        const classNameSave = (clickedBtn && typeof clickedBtn.className === 'string')
            ? clickedBtn.className
            : (typeof args.event?.target?.className === 'string' ? args.event.target.className : '');

        if (classNameSave.includes('e-event-cancel')) {
            args.cancel = false; // Laisse le popup se fermer

            const currentIdMaster = args.data.IdMaster || tempMasterData.current.IdMaster;
            const currentPinMaster = args.data.PinMaster || tempMasterData.current.PinMaster;

            if (currentIdMaster && currentPinMaster) {
                // REDIRECTION VERS LA NOUVELLE PAGE
                navigate(`/videowall/${currentIdMaster}/${currentPinMaster}`);
            } else {
                toast.info("Ce programme ne fait pas partie d'un VideoWall.", {
                    position: "top-center",
                    autoClose: 3000
                });
            }
            return;
        }

        // (domUrl / domDisp / domRep sont définis en haut de closePopup — lecture DOM = source de vérité.)
        const payload = {
            ...args.data,
            _pin_code: params._pin_code,

            Videourl1: domUrl(1),
            Videourl2: domUrl(2),
            Videourl3: domUrl(3),
            Videourl4: domUrl(4),
            Videourl5: domUrl(5),

            // Extension "X vidéos" : les slots dynamiques 6+ (e-fields Videourl6, Videourl7…)
            // sont sérialisés en JSON. null si aucun -> comportement historique inchangé.
            videosJson: (() => {
                const extra = [];
                for (let n = 6; Object.prototype.hasOwnProperty.call(args.data, `Videourl${n}`); n++) {
                    const u = domUrl(n);
                    if (u) extra.push(u);
                }
                return extra.length ? JSON.stringify(extra) : null;
            })(),

            // `repetition` assemblée depuis les inputs PAR LIGNE (repSlot1..N), dans l'ordre exact
            // que lisent les players : 5 slots historiques (toujours présents, même vides -> garde
            // l'alignement d'index avec Videourl1..5) PUIS une valeur par vidéo supplémentaire NON
            // VIDE (alignée avec videosJson). => repetition[i] correspond à la vidéo i pour Android
            // (allVideoSlots) comme pour la webapp.
            repetition: (() => {
                const norm = (v) => { const s = String(v ?? '').trim(); const n = parseInt(s, 10); return (n && n > 0) ? String(n) : '1'; };
                const reps = [];
                for (let n = 1; n <= 5; n++) reps.push(norm(domRep(n)));
                for (let n = 6; Object.prototype.hasOwnProperty.call(args.data, `Videourl${n}`); n++) {
                    if (domUrl(n)) reps.push(norm(domRep(n)));
                }
                return reps.join(';');
            })(),

            // `displayId` assemblé depuis les inputs PAR LIGNE (dispSlot1..N), MÊME alignement que
            // repetition : 5 positions historiques + une par vidéo supplémentaire non vide. Chaque
            // segment peut contenir plusieurs zones/dalles ('-'). Défaut '1' si vide (comme les players).
            displayId: (() => {
                const norm = (v) => { const s = String(v ?? '').trim(); return s || '1'; };
                const disp = [];
                for (let n = 1; n <= 5; n++) disp.push(norm(domDisp(n)));
                for (let n = 6; Object.prototype.hasOwnProperty.call(args.data, `Videourl${n}`); n++) {
                    if (domUrl(n)) disp.push(norm(domDisp(n)));
                }
                return disp.join(';');
            })(),

            StartTime: `${args.data.StartTime.getFullYear()}-${args.data.StartTime.getMonth() + 1}-${args.data.StartTime.getDate()} ${doubleZero(args.data.StartTime.getHours())}:${doubleZero(args.data.StartTime.getMinutes())}:${doubleZero(args.data.StartTime.getSeconds())}`,
            EndTime: `${args.data.EndTime.getFullYear()}-${args.data.EndTime.getMonth() + 1}-${args.data.EndTime.getDate()} ${doubleZero(args.data.EndTime.getHours())}:${doubleZero(args.data.EndTime.getMinutes())}:${doubleZero(args.data.EndTime.getSeconds())}`,
            noExp: args.data.noExp ? "True" : "False",
            twentyFourH: args.data.twentyFourH ? "True" : "False",
            RecurrenceRule: args.data.RecurrenceRule || '',


            IdMaster: args.data.IdMaster || tempMasterData.current.IdMaster,
            PinMaster: args.data.PinMaster || tempMasterData.current.PinMaster,
        };

        // Médias effectivement envoyés (5 legacy + extras videosJson) -> mis EN AVANT dans le toast :
        // c'est l'info capitale de la sauvegarde (quel contenu part réellement sur l'écran).
        const sentMedia = [payload.Videourl1, payload.Videourl2, payload.Videourl3, payload.Videourl4, payload.Videourl5]
            .concat((() => { try { const a = JSON.parse(payload.videosJson || '[]'); return Array.isArray(a) ? a : []; } catch { return []; } })())
            .filter((u) => u && String(u).trim())
            .map((u) => (String(u).split('?')[0].split('/').pop() || String(u)));
        const mediaToast = (title) => (
            <div>
                <div className="font-semibold">{title}</div>
                {sentMedia.length > 0 && (
                    <div className="mt-1 text-[13px]">
                        <div className="opacity-90">Média{sentMedia.length > 1 ? 's' : ''} envoyé{sentMedia.length > 1 ? 's' : ''} :</div>
                        <ul className="mt-0.5 list-disc pl-4 leading-snug">
                            {sentMedia.map((m, i) => <li key={i} className="break-all">{m}</li>)}
                        </ul>
                    </div>
                )}
            </div>
        );

        if (classNameSave.includes('e-event-save')) {
            if (actualIdEditRef.current) { // This is an UPDATE
                axios.put(`${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}/edit_programme`, { ...payload, Id: actualIdEditRef.current })
                    .then(() => {
                        const updatedData = data_programme.map(item => item.Id === actualIdEditRef.current ? { ...item, ...args.data } : item);
                        setProgramme(updatedData);
                        remountScheduler(); // repart d'un Scheduler sain -> prochaine édition OK sans recharger la page
                        toast.success(mediaToast("Programme mis à jour."), { autoClose: 8000 });
                    }).catch(err => console.log(err));
            } else { // This is a CREATE
                axios.post(`${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}/add_programme`, payload)
                    .then(res => {
                        // Add the new program with the ID from the server response
                        setProgramme([...data_programme, { ...args.data, Id: res.data.insertId }]);
                        remountScheduler();
                        toast.success(mediaToast("Programme ajouté."), { autoClose: 8000 });
                    }).catch(err => console.log(err));
            }
        } else if (classNameSave.includes('e-event-delete')) {
            if (args.data.Subject === "Default Programme") {
                toast.error("Le programme par défaut ne peut pas être supprimé.");
                return;
            }
            const data = { Id: actualIdEditRef.current, _pin_code: params._pin_code, IdMaster: args.data.IdMaster, PinMaster: args.data.PinMaster };
            axios.delete(`${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}/delete_programme`, { headers, data })
                .then(() => {
                    setProgramme(data_programme.filter(item => item.Id !== actualIdEditRef.current));
                    remountScheduler();
                    toast.success("Programme supprimé.");
                }).catch(err => console.log(err));
        }
    };

    const onPopupOpen = (args) => {
        // Éditeur ouvert -> on gèle les refresh du statut de download (cf. editorOpenRef) :
        // sinon un re-render recrée l'éditeur et efface la saisie en cours.
        if (args.type === 'Editor') editorOpenRef.current = true;
        if (args.data.Id !== undefined) {
            actualIdEditRef.current = args.data.Id
        } else {
            actualIdEditRef.current = undefined
        }

        tempMasterData.current.IdMaster = args.data.IdMaster;
        tempMasterData.current.PinMaster = args.data.PinMaster;

        if (args.type === 'Editor') {
            let statusElement = args.element.querySelector('#EventType');
            if (statusElement) {
                statusElement.setAttribute('name', 'EventType');
            }
        }

        axios.get(`${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}/listVideos`)
            .then(res => {
                datas = res.data.files
                    .filter((auth) => auth.fileName.endsWith('.mp4'))
                    .sort((a, b) => String(a.fileName).localeCompare(String(b.fileName), 'fr', { numeric: true, sensitivity: 'base' }))
            })
            .catch(err => console.log(err))

        const data = {
            token: token
        };

        axios.post(`${process.env.REACT_APP_IP2}:${process.env.REACT_APP_PORT2}/admin/auth/listeUsers`, data, { headers })
            .then(res => {
                clients = res.data;
            }).catch(err => console.log(err))
    }

    const changeExpiration = (e) => {
        console.log(e.checked)
        const boolValue = e.checked ? "True" : "False";
        let dateEnd = document.getElementById('EndTime');
        let dateStart = document.getElementById('StartTime');
        dateEnd.value = new Date(2099, 11, 31).toLocaleDateString("fr")
        if (boolValue === "True") {
            console.log("deficit")
            dateEnd.readOnly = true;
            dateEnd.style.backgroundColor = "lightgrey"
            recurrenceRef.current = 'FREQ=DAILY;INTERVAL=1;UNTIL=20991230T230000Z;'
        } else {
            dateEnd.value = dateStart.value
            dateEnd.style.backgroundColor = "white"
            recurrenceRef.current = ''
        }
    }

    const changeAllDay = (e) => {
        const boolValue = e.checked ? "True" : "False";
        let startHeure = document.getElementById("heureDebut")
        let endHeure = document.getElementById("heureFin")
        if (boolValue === "True") {
            startHeure.value = "00:00:00"
            endHeure.value = "23:59:59"
        } else {
            startHeure.value = ""
            endHeure.value = ""
        }
    }

    const dropdownVideo = (idDrop, vidoeKey) => (e) => {
        e.preventDefault();
        axios.get(`${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}/listVideos`)
            .then(res => {
                datas = res.data.files
                    .filter((auth) => auth.fileName.endsWith('.mp4'))
                    .sort((a, b) => String(a.fileName).localeCompare(String(b.fileName), 'fr', { numeric: true, sensitivity: 'base' }))
            })
            .catch(err => console.log(err))

        const data = {
            token: token
        };

        axios.post(`${process.env.REACT_APP_IP2}:${process.env.REACT_APP_PORT2}/admin/auth/listeUsers`, data, { headers })
            .then(res => {
                clients = res.data;
            }).catch(err => console.log(err))

        videokeyRef.current = vidoeKey
        let boxTest = document.getElementById("_dialog_wrapper");
        let element = document.getElementById(idDrop);

        if (element.style.display === "block") {
            element.style.display = "none"
            boxTest.style.width = "35rem"
        } else {
            boxTest.style.width = "55rem"
            element.style.display = "block"
        }
    }

    const setClientPdv = (clientIdTemp, idPdvTemp) => (e) => {
        e.preventDefault();
        let element = document.getElementById('dropdownPriceEat')
        let boxTest = document.getElementById("_dialog_wrapper");
        document.getElementById('clientId').value = clientIdTemp
        document.getElementById('idPdv').value = idPdvTemp
        element.style.display = "none"
        boxTest.style.width = "35rem"


    }

    const setVideoUrl = (urlVideo) => (e) => {
        e.preventDefault();
        let boxTest = document.getElementById("_dialog_wrapper");
        let element = document.getElementById('dropdown1');

        // Générique : gère VideourlN pour TOUT N (5 slots historiques + slots dynamiques 6+).
        // Remplace l'ancien switch hardcodé 1..5 (qui écrivait en plus values.Videourl2/3 sur
        // les cases 4/5 — bug). Le e-field est la source de vérité lue par Syncfusion au save.
        if (/^Videourl\d+$/.test(videokeyRef.current)) {
            const input = document.getElementById(videokeyRef.current);
            if (input) {
                input.value = urlVideo;
                // On propage `input` ET `change` : `input` synchronise le state React des slots
                // dynamiques (6+), `change` met à jour le modèle interne de Syncfusion (qui, sur
                // certaines versions, ne relit pas la valeur d'un e-field posée par programme sans un
                // évènement `change` -> le save renvoyait alors l'ancienne URL malgré la sélection).
                input.dispatchEvent(new Event('input', { bubbles: true }));
                input.dispatchEvent(new Event('change', { bubbles: true }));
            }
            values[videokeyRef.current] = urlVideo;
            element.style.display = "none";
            if (boxTest) boxTest.style.width = "35rem";
        }
    }

    function greaterThanZero(value) {
        if (value < 10) {
            return `0${value}`
        } else {
            return value
        }
    }

    function prePage() {
        if (currentPage !== 1) {
            currentPage = currentPage - 1
            lastIndex = currentPage * recordsPerPage;
            firstindex = lastIndex - recordsPerPage;
            const query = searchRef.current.value.toLowerCase();
            if (query === '') {
                displayResults(datas, firstindex, lastIndex);
            } else {
                const filteredResults = datas.filter(item =>
                    item.fileName.toLowerCase().includes(query)
                )
                displayResults(filteredResults, firstindex, lastIndex);
            }
        }
    }

    function changeCurrPage(id) {
        currentPage = id;
        lastIndex = id * recordsPerPage;
        firstindex = lastIndex - recordsPerPage
        const query = searchRef.current.value.toLowerCase();
        if (query === '') {
            displayResults(datas, firstindex, lastIndex);
        } else {
            const filteredResults = datas.filter(item =>
                item.fileName.toLowerCase().includes(query)
            )
            displayResults(filteredResults, firstindex, lastIndex);
        }
    }

    function nextPage() {
        if (currentPage !== npage) {
            currentPage = currentPage + 1
            lastIndex = currentPage * recordsPerPage;
            firstindex = lastIndex - recordsPerPage;
            const query = searchRef.current.value.toLowerCase();
            if (query === '') {
                displayResults(datas, firstindex, lastIndex);
            } else {
                const filteredResults = datas.filter(item =>
                    item.fileName.toLowerCase().includes(query)
                )
                displayResults(filteredResults, firstindex, lastIndex);
            }
        }
    }

    function prePageClients() {
        if (currentClientPage !== 1) {
            currentClientPage = currentClientPage - 1
            lastClientIndex = currentClientPage * recordsPerPage;
            firstClientIndex = lastClientIndex - recordsPerPage;
            const query = searchClientRef.current.value.toLowerCase();
            if (query === '') {
                displayClientsResults(clients, firstClientIndex, lastClientIndex);
            } else {
                const filteredResults = clients.filter(item =>
                    item.username.toLowerCase().includes(query) || item.id_pdv.toLowerCase().includes(query)
                )
                displayClientsResults(filteredResults, firstClientIndex, lastClientIndex);
            }
        }
    }

    function changeCurrPageClients(id) {
        currentClientPage = id;
        lastClientIndex = id * recordsPerPage;
        firstClientIndex = lastClientIndex - recordsPerPage
        const query = searchClientRef.current.value.toLowerCase();
        if (query === '') {
            displayClientsResults(clients, firstClientIndex, lastClientIndex);
        } else {
            const filteredResults = clients.filter(item =>
                item.username.toLowerCase().includes(query) || item.id_pdv.toLowerCase().includes(query)
            )
            displayClientsResults(filteredResults, firstClientIndex, lastClientIndex);
        }
    }

    function nextPageClients() {
        if (currentClientPage !== npage) {
            currentClientPage = currentClientPage + 1
            lastClientIndex = currentClientPage * recordsPerPage;
            firstClientIndex = lastClientIndex - recordsPerPage;
            const query = searchClientRef.current.value.toLowerCase();
            if (query === '') {
                displayClientsResults(clients, firstClientIndex, lastClientIndex);
            } else {
                const filteredResults = clients.filter(item =>
                    item.username.toLowerCase().includes(query) || item.id_pdv.toLowerCase().includes(query)
                )
                displayClientsResults(filteredResults, firstClientIndex, lastClientIndex);
            }
        }
    }

    // Recherche vidéo — LIVE (onInput) et « CONTIENT » (n'importe où dans le nom, pas seulement au
    // début). Repart page 1 à chaque frappe pour ne jamais afficher une page vide de résultats.
    const handleSearch = (event) => {
        if (event && event.preventDefault) event.preventDefault();
        const query = (searchRef.current && searchRef.current.value ? searchRef.current.value : '').toLowerCase();
        currentPage = 1; firstindex = 0; lastIndex = recordsPerPage;
        const filteredResults = query
            ? datas.filter(item => item.fileName.toLowerCase().includes(query))
            : datas;
        displayResults(filteredResults, firstindex, lastIndex);
    }

    // Ferme le picker vidéo (croix) sans rien sélectionner ; restaure la largeur du dialog.
    const closePicker = () => {
        const el = document.getElementById('dropdown1');
        const boxTest = document.getElementById('_dialog_wrapper');
        if (el) el.style.display = 'none';
        if (boxTest) boxTest.style.width = '35rem';
    }

    const displayResults = (results, firstindex, lastIndex) => {
        if (resultsRef.current) {
            resultsRef.current.innerHTML = ''
            if (results.length > 0) {
                let tableBody = document.getElementById("searchBody");
                let tr;
                let thName;
                let tdDate;
                let tdSize;
                results.slice(firstindex, lastIndex).map((video, i) => (
                    tr = document.createElement('tr'),
                    tr.classList.add('cursor-pointer', 'border-b', 'border-slate-100', 'transition', 'hover:bg-indigo-50'),
                    tr.key = i,
                    tr.onclick = setVideoUrl(process.env.REACT_APP_DL_IP + 'MOV/' + video.fileName),

                    thName = document.createElement('th'),
                    thName.scope = "row",
                    thName.classList.add('px-4', 'py-2.5', 'text-left', 'font-medium', 'text-slate-800', 'whitespace-nowrap'),
                    thName.textContent = video.fileName,

                    tdDate = document.createElement('td'),
                    tdDate.classList.add('px-4', 'py-2.5', 'text-slate-500', 'whitespace-nowrap'),
                    tdDate.textContent = greaterThanZero(new Date(video.fileDate).getUTCDate()) + '/' + greaterThanZero(new Date(video.fileDate).getUTCMonth() + 1) + '/' + greaterThanZero(new Date(video.fileDate).getUTCFullYear()) + ' ' + greaterThanZero(new Date(video.fileDate).getUTCHours() + 2) + ':' + greaterThanZero(new Date(video.fileDate).getUTCMinutes()),

                    tdSize = document.createElement('td'),
                    tdSize.classList.add('px-4', 'py-2.5', 'text-slate-500', 'font-mono', 'text-xs', 'whitespace-nowrap'),
                    tdSize.textContent = video.fileSize,
                    tr.appendChild(thName),
                    tr.appendChild(tdDate),
                    tr.appendChild(tdSize),
                    tableBody.appendChild(tr)
                ))
            }
        }
    }

    // Tri du picker (Nom / Date / Taille) — impératif comme la pagination (aucun state, sinon
    // Syncfusion viderait l'éditeur ouvert). Re-clic sur la même colonne = inverse le sens.
    const sortPicker = (key) => {
        const s = pickerSortRef.current;
        s.dir = s.key === key ? -s.dir : 1;
        s.key = key;
        const dir = s.dir;
        datas.sort((a, b) => {
            if (key === 'fileSize') return (sizeToBytes(a.fileSize) - sizeToBytes(b.fileSize)) * dir;
            if (key === 'fileDate') return (new Date(a.fileDate).getTime() - new Date(b.fileDate).getTime()) * dir;
            return String(a.fileName).localeCompare(String(b.fileName), 'fr', { numeric: true, sensitivity: 'base' }) * dir;
        });
        currentPage = 1;
        lastIndex = recordsPerPage;
        firstindex = 0;
        const query = (searchRef.current && searchRef.current.value ? searchRef.current.value : '').toLowerCase();
        const rows = query ? datas.filter((v) => v.fileName.toLowerCase().includes(query)) : datas;
        displayResults(rows, firstindex, lastIndex);
    };

    const handleClientSearch = (event) => {
        event.preventDefault();
        const query = searchClientRef.current.value.toLowerCase();
        const filteredResults = clients.filter(item =>
            item.username.toLowerCase().includes(query) || item.id_pdv.toLowerCase().includes(query)
        )
        displayClientsResults(filteredResults, firstClientIndex, lastClientIndex);

    }

    const displayClientsResults = (results, firstClientIndex, lastClientIndex) => {
        if (resultsClientRef.current) {
            resultsClientRef.current.innerHTML = ''
            if (results.length > 0) {
                let tableBody = document.getElementById("searchClientBody");
                let tr;
                let thUsername;
                let tdIdpdv;
                results.slice(firstClientIndex, lastClientIndex).map((client, i) => (
                    tr = document.createElement('tr'),
                    tr.classList.add('bg-white', 'border-b', 'dark:bg-slate-800', 'dark:border-slate-700'),
                    tr.key = i,
                    tr.onclick = setClientPdv(client.username, client.id_pdv),

                    thUsername = document.createElement('th'),
                    thUsername.scope = "row",
                    thUsername.classList.add('px-6', 'py-4', 'font-medium', 'text-slate-900', 'whitespace-nowrap', 'dark:text-white'),
                    thUsername.textContent = client.username,

                    tdIdpdv = document.createElement('td'),
                    tdIdpdv.classList.add('px-6', 'py-4'),
                    tdIdpdv.textContent = client.id_pdv,

                    tr.appendChild(thUsername),
                    tr.appendChild(tdIdpdv),
                    tableBody.appendChild(tr)
                ))
            }
        }
    }

    const onChange = (args) => {
        recurrenceRef.current = args.value;
    }

    const waitForElement = (id, timeout = 5000) => {
        return new Promise((resolve, reject) => {
            const interval = 100;
            let elapsed = 0;
            const checkExistence = setInterval(() => {
                const element = document.getElementById(id);
                if (element) {
                    clearInterval(checkExistence);
                    resolve(element);
                }
                elapsed += interval;
                if (elapsed >= timeout) {
                    clearInterval(checkExistence);
                    reject(new Error(`Element with ID "${id}" not found within timeout`));
                }
            }, interval);
        });
    };

    const newProgramme = async (e) => {
        e.preventDefault()
        if (scheduleObj.current) {
            const scheduleObject = scheduleObj.current
            scheduleObject.openEditor({}, "Add");
            try {
                const dateEnd = await waitForElement('EndTime');
                const dateStart = await waitForElement('StartTime');

                console.log(dateEnd);
                console.log(dateStart);

                // Optionally set default values
                dateEnd.value = new Date().toLocaleDateString("fr");
                dateStart.value = new Date().toLocaleDateString("fr");
            } catch (error) {
                console.error(error.message);
            }
        }
    }

    const editorTemplate = (props) => {
        // Répétition par ligne : on éclate le CSV `repetition` existant pour pré-remplir chaque
        // input (index 0..4 = Vidéo 1..5, index 5+ = vidéos supplémentaires dans l'ordre).
        const repParts = String(props.repetition || '').split(';');
        const repAt = (n) => {
            const v = (repParts[n - 1] || '').trim();
            return v && !Number.isNaN(Number(v)) ? v : '1';
        };
        // display_id PAR LIGNE : une valeur par position (segment ';' du CSV displayId), un segment
        // pouvant contenir plusieurs zones/dalles avec '-'. Aligné sur la vidéo i, comme la répétition.
        const dispParts = String(props.displayId || '').split(';');
        const dispAt = (n) => (dispParts[n - 1] || '').trim();
        const getFileName = (url) => {
            if (!url || typeof url !== 'string') return '';
            return url.substring(url.lastIndexOf('/') + 1);
        };

        const getProgress = (videoUrl, initialProgress) => {
            const fileName = getFileName(videoUrl);
            const liveProgress = downloadProgress[fileName];

            // Prioritize live progress, then initial prop, then fallback to 0
            const progress = liveProgress ?? initialProgress ?? 0;

            if (progress === 100) return '100%';
            if (progress > 0) return `${progress}%`;

            // Return initial value if it's 0, otherwise null to show nothing
            return initialProgress === 0 ? '0%' : null;
        };

        // Boutons contextuels COMPACTS (reco 2) : relogés dans les en-têtes de section
        // (Videowall -> Playlist, Price It -> Price-Eat) au lieu de flotter en haut, coupés au scroll.
        const priceItBtn = (
            <button type="button"
                className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${(props.clientId && props.idPdv)
                    ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-slate-200 text-slate-400 cursor-not-allowed"}`}
                onClick={() => handlePriceItClick(props)}
                title={(!props.clientId || !props.idPdv) ? "Client ID ou ID PDV manquant" : "Aller au Price It"}>
                🛒 Price It
            </button>
        );
        const videowallBtn = (
            <button type="button"
                className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${(props.IdMaster && props.PinMaster)
                    ? "bg-purple-600 text-white hover:bg-purple-700" : "bg-slate-200 text-slate-400 cursor-not-allowed"}`}
                onClick={() => handleVideoWallClick(props)}
                title={(!props.IdMaster || !props.PinMaster) ? "Aucun groupe associé" : "Voir le groupe"}>
                🖥️ Videowall
            </button>
        );

        return (
            <div>
                <input type="hidden" id="IdMaster" name="IdMaster" defaultValue={props.IdMaster} />
                <input type="hidden" id="PinMaster" name="PinMaster" defaultValue={props.PinMaster} />

                <div id="dropdown1" className="z-50 hidden absolute top-14 left-1/2 -translate-x-1/2 w-11/12 max-w-3xl max-h-[70vh] overflow-auto rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200">
                    {/* Recherche (sticky) — LIVE + "contient" (n'importe où dans le nom). Croix pour fermer. */}
                    <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
                        <div className="flex items-center gap-2">
                            <div className="relative flex flex-grow items-center">
                                <svg onClick={handleSearch} xmlns="http://www.w3.org/2000/svg" className="absolute left-3 h-5 w-5 cursor-pointer text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <input
                                    className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-800 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/20"
                                    type="text"
                                    id="search"
                                    placeholder="Rechercher (n'importe où dans le nom)…"
                                    autoComplete="off"
                                    ref={searchRef}
                                    onInput={handleSearch}
                                />
                            </div>
                            <button type="button" onClick={closePicker} title="Fermer sans sélectionner"
                                className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-slate-400 transition hover:bg-slate-100 hover:text-slate-700">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                    </div>

                    <table className="w-full text-left text-sm text-slate-600">
                        <thead className="sticky top-0 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                            <tr>
                                <th scope="col" className="cursor-pointer select-none px-4 py-2.5 font-semibold hover:text-indigo-600" onClick={() => sortPicker('fileName')} title="Trier par nom">Nom du fichier ⇅</th>
                                <th scope="col" className="cursor-pointer select-none px-4 py-2.5 font-semibold hover:text-indigo-600" onClick={() => sortPicker('fileDate')} title="Trier par date">Date ⇅</th>
                                <th scope="col" className="cursor-pointer select-none px-4 py-2.5 font-semibold hover:text-indigo-600" onClick={() => sortPicker('fileSize')} title="Trier par taille">Taille ⇅</th>
                            </tr>
                        </thead>
                        <tbody ref={resultsRef} id='searchBody'>
                            {datas.slice(firstindex, lastIndex).map((video, i) => (
                                <tr className="cursor-pointer border-b border-slate-100 transition last:border-0 hover:bg-indigo-50" key={i} onClick={setVideoUrl(process.env.REACT_APP_DL_IP + 'MOV/' + video.fileName)} >
                                    <th scope="row" className="px-4 py-2.5 text-left font-medium text-slate-800">
                                        <span className="inline-flex items-center gap-2">
                                            <svg className="h-4 w-4 shrink-0 text-indigo-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" /></svg>
                                            {video.fileName}
                                        </span>
                                    </th>
                                    <td className="whitespace-nowrap px-4 py-2.5 text-slate-500">
                                        {greaterThanZero(new Date(video.fileDate).getUTCDate())}/{greaterThanZero(new Date(video.fileDate).getUTCMonth())}/{greaterThanZero(new Date(video.fileDate).getUTCFullYear())}  {greaterThanZero(new Date(video.fileDate).getUTCHours() + 2)}:{greaterThanZero(new Date(video.fileDate).getUTCMinutes())}
                                    </td>
                                    <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs text-slate-500">
                                        {video.fileSize}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <nav aria-label="Pagination" className='sticky bottom-0 border-t border-slate-200 bg-white/95 px-4 py-2 text-center backdrop-blur'>
                        <ul className="inline-flex -space-x-px text-sm">
                            <li>
                                <a className="flex items-center justify-center px-3 h-8 leading-tight text-slate-500 bg-white border border-slate-300 rounded-l-lg cursor-pointer hover:bg-indigo-50 hover:text-indigo-700" onClick={prePage} >Précédent</a>
                            </li>

                            {numbers.slice(0, 10).map((n) => {
                                return <li key={n}>
                                    <a className={`flex items-center justify-center px-3 h-8 leading-tight border border-slate-300 cursor-pointer hover:bg-indigo-50 hover:text-indigo-700 ${n === currentPage ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-600 hover:text-white' : 'bg-white text-slate-500'}`} onClick={() => changeCurrPage(n)}>{n}</a>
                                </li>
                            })}

                            <li>
                                <a className="flex items-center justify-center px-3 h-8 leading-tight text-slate-500 bg-white border border-slate-300 rounded-r-lg cursor-pointer hover:bg-indigo-50 hover:text-indigo-700" onClick={nextPage} >Suivant</a>
                            </li>
                        </ul>
                    </nav>
                </div>
                <div id="dropdownPriceEat" className="z-40 hidden absolute top-8 right-8 bg-white divide-y rounded-lg shadow w-11/12 h-3/4 dark:bg-slate-700 cursor-pointer overflow-auto">
                    <div className="relative flex items-center left-1/4 w-2/4 h-9 rounded-lg focus-within:shadow-lg bg-slate-700 overflow-hidden my-2">
                        <div className="grid place-items-center h-full w-12 text-slate-50" onClick={handleClientSearch}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>

                        <input
                            className="h-full w-full outline-none text-white pr-2 bg-slate-700 placeholder-slate-50"
                            type="text"
                            id="search"
                            placeholder="Search something.."
                            ref={searchClientRef}
                        // onChange={(e) => { setSearchPriceEat(e.target.value); setCurrentPage(1) }}
                        />
                    </div>
                    <table className="w-full text-sm text-left text-slate-500 dark:text-slate-400">
                        <thead className="text-xs text-slate-700 uppercase bg-slate-50 dark:bg-slate-700 dark:text-slate-400">
                            <tr>
                                <th scope="col" className="px-6 py-3">
                                    Client ID
                                </th>
                                <th scope="col" className="px-6 py-3">
                                    ID PDV
                                </th>
                            </tr>
                        </thead>
                        <tbody ref={resultsClientRef} id='searchClientBody'>
                            {
                                clients.slice(firstindex, lastIndex).map((client, i) => (
                                    <tr className="bg-white border-b dark:bg-slate-800 dark:border-slate-700" key={i} onClick={setClientPdv(client.username, client.id_pdv)} >
                                        <th scope="row" className="px-6 py-4 font-medium text-slate-900 whitespace-nowrap dark:text-white">
                                            {client.username}
                                        </th>
                                        <td className="px-6 py-4">
                                            {client.id_pdv}
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                    <nav aria-label="Page navigation example" className='text-center bottom-4 sticky'>
                        <ul className="inline-flex -space-x-px text-sm">
                            <li>
                                <a className="flex items-center justify-center px-3 h-8 ml-0 leading-tight text-slate-500 bg-white border border-slate-300 rounded-l-lg hover:bg-slate-100 hover:text-slate-700 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-white" onClick={prePageClients} >Previous</a>
                            </li>

                            {numbers.slice(0, 10).map((n, i) => {
                                return <li>
                                    <a className="flex items-center justify-center px-3 h-8 leading-tight text-slate-500 bg-white border border-slate-300 hover:bg-slate-100 hover:text-slate-700 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-white" onClick={() => changeCurrPageClients(n)}>{n}</a>
                                </li>
                            })}

                            <li>
                                <a className="flex items-center justify-center px-3 h-8 leading-tight text-slate-500 bg-white border border-slate-300 rounded-r-lg hover:bg-slate-100 hover:text-slate-700 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-white" onClick={nextPageClients} >Next</a>
                            </li>
                        </ul>
                    </nav>
                </div>


                {/* Formulaire restylé : mêmes ids / names / composants / handlers (AUCUN changement
                    de logique) — labels en français, vidéos factorisées, sections visuelles. */}
                <table className="custom-event-editor w-full" ><tbody>
                    <tr><td colSpan={5} className="pe-section">📋 Programme</td></tr>
                    <tr><td className="e-textlabel">Titre </td><td colSpan={4}>
                        <input id="Summary" className="e-field e-input w-full" type="text" name="Subject" defaultValue={props.Subject} />
                    </td></tr>

                    <tr><td colSpan={5} className="pe-section">
                        <div className="flex items-center justify-between gap-2">
                            <span>🎬 Playlist — vidéo &amp; nombre de passages</span>
                            {videowallBtn}
                        </div>
                    </td></tr>
                    <tr><td colSpan={5}>
                        <div className="pe-hint" style={{ marginTop: 0, marginBottom: 4, lineHeight: 1.5 }}>
                            Pour chaque vidéo : <b>🔍</b> = choisir depuis la médiathèque ·
                            <b> écran</b> = zone d'affichage / <i>display id</i> (ex. <code>1</code>, <code>1-2-3-4</code> pour un mur d'écrans, <code>99</code>) ·
                            <b> ×</b> = nombre de passages dans la boucle.
                        </div>
                    </td></tr>
                    {/* En-têtes de colonnes alignés : flex-grow = zone URL, puis 2 champs fixes compacts. */}
                    <tr><td></td><td colSpan={4}>
                        <div className="flex items-center w-full gap-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                            <span className="flex-grow">Vidéo — URL / médiathèque</span>
                            <span className="shrink-0 text-center" style={{ width: '84px' }} title="Identifiant d'écran / display id">id écran</span>
                            <span className="shrink-0 text-center" style={{ width: '52px' }} title="Nombre de répétitions">répét.</span>
                        </div>
                    </td></tr>
                    {[1, 2, 3, 4, 5].map((n) => {
                        const url = props[`Videourl${n}`];
                        const fname = getFileName(url);
                        return (
                        <tr key={`vid-${n}`} className={url ? '' : 'opacity-60'}><td className="e-textlabel align-top">
                            <div>Vidéo {n}</div>
                            {fname && (
                                <div className="mt-0.5 flex items-center gap-1">
                                    <span className="max-w-[90px] truncate text-[10px] font-semibold text-indigo-500" title={url}>{fname}</span>
                                    <button type="button" title="Aperçu de la vidéo" onClick={() => openPreview(url)} className="shrink-0 text-slate-400 hover:text-indigo-600">
                                        <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5"><path d="M6.3 2.8A1 1 0 0 0 4.8 3.6v12.8a1 1 0 0 0 1.5.9l10-6.4a1 1 0 0 0 0-1.7l-10-6.4Z" /></svg>
                                    </button>
                                    <button type="button" title="Copier le nom" onClick={() => copyMediaName(fname)} className="shrink-0 text-slate-400 hover:text-indigo-600">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-3.5 w-3.5"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>
                                    </button>
                                </div>
                            )}
                        </td><td colSpan={4}>
                            <div className="flex items-center w-full gap-2">
                                <div className="flex flex-grow items-center gap-2 min-w-0">
                                    <input id={`Videourl${n}`} className="e-field e-input pe-url" type="text" name={`Videourl${n}`} defaultValue={url} title={url || undefined} placeholder="URL de la vidéo (loupe = médiathèque)" />
                                    <span className="text-xs font-semibold text-green-600 whitespace-nowrap">
                                        {getProgress(url, props[`percentageVideourl${n}`])}
                                    </span>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 shrink-0 cursor-pointer text-slate-400 hover:text-indigo-600" onClick={dropdownVideo('dropdown1', `Videourl${n}`)} >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                                    </svg>
                                </div>
                                {/* display_id (id écran) PAR LIGNE — compact. Répétition PAR LIGNE (max 2 chiffres). */}
                                <input id={`dispSlot${n}`} name={`dispSlot${n}`} list="displayIds" className="e-field e-input pe-disp text-center" type="text" title={`id écran / display id de la vidéo ${n} (ex. 1 ou 1-2-3-4)`} placeholder="1" defaultValue={dispAt(n)} />
                                <input id={`repSlot${n}`} name={`repSlot${n}`} className="e-field e-input pe-rep text-center" type="number" min="1" max="99" title={`Nombre de répétitions de la vidéo ${n}`} defaultValue={repAt(n)} />
                            </div>
                        </td></tr>
                        );
                    })}
                    {/* Extension "X vidéos" : slots 6+ dynamiques, chacun avec sa répétition + display_id. */}
                    <ExtraVideoSlots initialJson={props.videosJson} initialReps={repParts.slice(5)} initialDisps={dispParts.slice(5)} onOpenPicker={(field) => dropdownVideo('dropdown1', field)} onPreview={openPreview} onCopy={copyMediaName} />
                    <datalist id="displayIds">
                        <option value="1"></option>
                        <option value="1-2-3-4"></option>
                        <option value="99"></option>
                    </datalist>

                    <tr><td colSpan={5} className="pe-section">
                        <div className="flex items-center justify-between gap-2">
                            <span>💶 Price-Eat &amp; écrans</span>
                            {priceItBtn}
                        </div>
                    </td></tr>
                    <tr><td className="e-textlabel">Client Price-Eat </td><td colSpan={4}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 right-1 absolute cursor-pointer text-slate-400 hover:text-indigo-600" onClick={dropdownVideo('dropdownPriceEat', '')} >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                        </svg>
                        <input id="clientId" className="e-field e-input " type="text" name="clientId" defaultValue={props.clientId} />
                    </td></tr>
                    <tr><td className="e-textlabel">ID PDV </td><td colSpan={4}>
                        <input id="idPdv" className="e-field e-input " type="text" name="idPdv" defaultValue={props.idPdv} />
                    </td></tr>
                    <tr><td></td><td colSpan={4}>
                        <p className="pe-hint" style={{ marginTop: 0 }}>Les écrans (display id) se règlent désormais <b>par vidéo</b> dans la playlist ci-dessus.</p>
                    </td></tr>

                    <tr><td colSpan={5} className="pe-section">🗓️ Diffusion</td></tr>
                    <tr><td className="e-textlabel">Période <span className="pe-hint">du / au</span></td><td>
                        <DatePickerComponent format='dd/MM/yyyy' id="StartTime" data-name="StartTime" value={new Date(props.startTime || props.StartTime)} className="e-field"></DatePickerComponent>
                    </td>
                        <td>
                            <DatePickerComponent format='dd/MM/yyyy' id="EndTime" data-name="EndTime" value={new Date(props.endTime || props.EndTime)} className="e-field"></DatePickerComponent>
                        </td></tr>
                    <tr><td className="e-textlabel">Sans date de fin</td><td colSpan={4}>
                        <CheckBoxComponent
                            id="noExp"
                            data-name="noExp"
                            className="e-field"
                            checked={props.noExp === "True"}
                            change={(args) => changeExpiration(args)}
                        />
                    </td></tr>
                    <tr><td className="e-textlabel">Plage horaire <span className="pe-hint">début / fin</span></td><td>
                        <input type="time" id="heureDebut" name="heureDebut" className="e-field " step="1" defaultValue={props.heureDebut} />
                    </td>
                        <td>
                            <input type="time" id="heureFin" name="heureFin" className="e-field " step="1" defaultValue={props.heureFin} />
                        </td></tr>
                    <tr><td className="e-textlabel">Toute la journée <span className="pe-hint">24 h</span></td><td colSpan={4}>
                        <CheckBoxComponent
                            id="twentyFourH"
                            data-name="twentyFourH"
                            className="e-field"
                            checked={props.twentyFourH === "True"}
                            change={(args) => changeAllDay(args)}
                        />
                    </td></tr>
                    <tr><td className="e-textlabel">Récurrence </td><td colSpan={4}>
                        <RecurrenceEditorComponent id='RecurrenceRule' data-name="RecurrenceRule" className="e-event-save e-field" name="RecurrenceRule" value={props.RecurrenceRule} change={onChange}></RecurrenceEditorComponent>
                    </td></tr>
                </tbody></table></div>);
    }

    return (
        <div>
            <main className="flex flex-row  mt-12 ">
                <div>
                    <div className='flex flex-row justify-between'>
                        <div>
                            <button type="button" className="ml-4 text-white bg-slate-800 hover:bg-slate-900 focus:outline-none focus:ring-4 focus:ring-slate-300 font-medium rounded-lg text-sm px-5 py-2.5 mr-2 mb-2 dark:bg-slate-800 dark:hover:bg-slate-700 dark:focus:ring-slate-700 dark:border-slate-700 float-left" onClick={() => { setShowModal(true); }}>Copier un programme</button>
                            <button type="button" className="ml-4 text-white bg-slate-800 hover:bg-slate-900 focus:outline-none focus:ring-4 focus:ring-slate-300 font-medium rounded-lg text-sm px-5 py-2.5 mr-2 mb-2 dark:bg-slate-800 dark:hover:bg-slate-700 dark:focus:ring-slate-700 dark:border-slate-700 float-left" onClick={newProgramme}>Ajouter un programme</button>
                        </div>
                        <h2 className='text-xl font-bold tracking-tight text-slate-900'>{clientName ? clientName : "Unknown"} - {params._pin_code}</h2>
                        <img src={logoGif} height={50} width={50} alt="Info" className='float-right cursor-pointer' onClick={() => { setModalInfo(true) }} />
                    </div>
                    <div className="mx-4 mb-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                        <h3 className="text-base font-semibold text-slate-800">Suivi Download Device</h3>
                        {isDownloadLoading ? (
                            <p className="mt-2 text-sm text-slate-500">Chargement du suivi...</p>
                        ) : (
                            <div className="mt-3">
                                <div className="mb-3">
                                    <div className="mb-1 flex items-center justify-between text-sm text-slate-700">
                                        <span>Progression globale</span>
                                        <span>{safeProgress(deviceDownloadStatus?.aggregate?.globalProgress)}%</span>
                                    </div>
                                    <div className="h-2.5 w-full rounded-full bg-slate-200">
                                        <div
                                            className="h-2.5 rounded-full bg-blue-600"
                                            style={{ width: `${safeProgress(deviceDownloadStatus?.aggregate?.globalProgress)}%` }}
                                        />
                                    </div>
                                </div>
                                <div className="mb-3 grid grid-cols-2 gap-2 text-xs text-slate-700 md:grid-cols-5">
                                    <div className="rounded border border-slate-200 bg-slate-50 p-2">OK: {deviceDownloadStatus?.aggregate?.filesOk || 0}</div>
                                    <div className="rounded border border-slate-200 bg-slate-50 p-2">Failed: {deviceDownloadStatus?.aggregate?.filesFailed || 0}</div>
                                    <div className="rounded border border-slate-200 bg-slate-50 p-2">Pending: {deviceDownloadStatus?.aggregate?.filesPending || 0}</div>
                                    <div className="rounded border border-slate-200 bg-slate-50 p-2">Retrying: {deviceDownloadStatus?.aggregate?.filesRetrying || 0}</div>
                                    <div className="rounded border border-slate-200 bg-slate-50 p-2">ETA: {formatEta(deviceDownloadStatus?.aggregate?.etaSeconds)}</div>
                                </div>
                                <div className="mb-2 text-[11px] text-slate-500">
                                    Session: {deviceDownloadStatus?.sessionId || "n/a"}
                                </div>
                                <div className="max-h-52 overflow-auto rounded border border-slate-200">
                                    <table className="w-full text-left text-xs">
                                        <thead className="sticky top-0 bg-slate-50 text-slate-600">
                                            <tr>
                                                <th className="px-3 py-2">Fichier</th>
                                                <th className="px-3 py-2">Statut</th>
                                                <th className="px-3 py-2">ETA</th>
                                                <th className="px-3 py-2">Tentative</th>
                                                <th className="px-3 py-2">Raison</th>
                                                <th className="px-3 py-2">Terminé à</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(deviceDownloadStatus?.files || []).map((file) => (
                                                <tr key={`${file.sessionId}-${file.fileName}`} className="border-t border-slate-100">
                                                    <td className="px-3 py-2 font-medium text-slate-800">{file.fileName}</td>
                                                    <td className="px-3 py-2">
                                                        <span className={`rounded px-2 py-1 text-[11px] font-semibold ${DOWNLOAD_STATUS_COLORS[file.status] || "bg-slate-100 text-slate-700"}`}>
                                                            {file.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 text-slate-700">{formatEta(file.estimatedRemainingSec)}</td>
                                                    <td className="px-3 py-2 text-slate-700">{file.attempt || 0}</td>
                                                    <td className="px-3 py-2 text-red-600">{file.reason || file.errorCode || "-"}</td>
                                                    <td className="px-3 py-2 whitespace-nowrap text-slate-500">{formatDoneAt(file)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                    {isLoading ? (
                        <div className="text-center p-10">
                            <p>Chargement du programme en cours...</p>
                        </div>
                    ) : (
                        <MemoizedScheduler key={schedulerKey} ref={scheduleObj} selectedDate={new Date()} eventSettings={eventSettings} locale='fr-CH' editorTemplate={editorTemplate.bind(this)} popupOpen={onPopupOpen.bind(this)} popupClose={closePopup.bind(this)} showQuickInfo={false} showRecurrenceAlert={false} >
                            <ViewsDirective>
                                <ViewDirective option='Week' />
                                <ViewDirective option='Month' />
                            </ViewsDirective>
                            <Inject services={[Week, Month, DragAndDrop]} />
                        </MemoizedScheduler>
                    )}
                </div>
            </main>
            <ToastContainer position="top-center" autoClose={6000} newestOnTop pauseOnHover
                theme="colored" style={{ width: 'auto', maxWidth: '92vw' }} />

            {/* Aperçu vidéo — STATIQUE + toggle DOM (openPreview/closePreview), PAS de state React :
                ouvrir l'aperçu ne re-render pas Programme -> l'éditeur Syncfusion garde sa saisie.
                z-index très élevé pour passer au-dessus du dialog Syncfusion. */}
            <div id="videoPreviewModal" className="hidden fixed inset-0 flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm"
                 style={{ zIndex: 100000 }}
                 onClick={(e) => { if (e.currentTarget === e.target) closePreview(); }}>
                <div className="w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl">
                    <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
                        <h3 id="videoPreviewTitle" className="truncate text-sm font-semibold text-slate-800"></h3>
                        <button type="button" onClick={closePreview}
                                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">✕</button>
                    </div>
                    <div className="bg-black">
                        <video id="videoPreviewPlayer" controls className="max-h-[75vh] w-full" />
                    </div>
                </div>
            </div>
            <ModalCopyProgram isVisible={showModal} onClose={() => setShowModal(false)} choosenPinCode={params._pin_code} />
            <ModalInfo isVisible={modalInfo} onClose={() => setModalInfo(false)} />

        </div>
    )
}

export default Programme;

